<template>
  <div class="course-maker">
    <Sidebar />
    <div class="course-maker-content">
      <div class="course-maker-header">
        <h1>Create Course</h1>
      </div>

      <div v-if="successMessage" class="alert alert-success">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>

      <form @submit.prevent="submitCourse" class="course-form">
        <!-- Navigation tabs -->
        <div class="form-tabs">
          <button 
            type="button" 
            v-for="(section, index) in formSections" 
            :key="index" 
            @click="activeSection = index"
            :class="{ active: activeSection === index }"
            class="tab-button"
          >
            {{ section }}
          </button>
        </div>

        <!-- Basic Information -->
        <div class="form-section-container" v-show="activeSection === 0">
          <h2>Basic Information</h2>
          
          <div class="form-grid">
            <div class="form-group">
              <label for="title">Course Title <span class="required">*</span></label>
              <input
                id="title"
                v-model="formData.title"
                type="text"
                required
              />
            </div>
            
            <div class="form-group">
              <label for="title_ar">Course Title (Arabic) <span class="required">*</span></label>
              <input
                id="title_ar"
                v-model="formData.title_ar"
                type="text"
                dir="rtl"
                lang="ar"
                required
              />
            </div>
          </div>

          <div class="form-group">
            <label for="description">Course Description <span class="required">*</span></label>
            <QuillRichTextEditor
              id="description"
              v-model="formData.description"
              label=""
              placeholder="Enter course description"
              :minHeight="'250px'"
              required
            />
          </div>

          <div class="form-group">
            <label for="description_ar">Course Description (Arabic) <span class="required">*</span></label>
            <QuillRichTextEditor
              id="description_ar"
              v-model="formData.description_ar"
              label=""
              placeholder="أدخل وصف الدورة"
              :minHeight="'250px'"
              :toolbar="arabicToolbar"
              required
            />
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label for="instructor_id">Instructor <span class="required">*</span></label>
              <select
                id="instructor_id"
                v-model="formData.instructor_id"
                required
                :disabled="isLoading"
              >
                <option value="">{{ isLoading ? 'Loading instructors...' : 'Select an instructor' }}</option>
                <option v-for="instructor in instructors" :key="instructor.id" :value="instructor.user_id">
                  {{ instructor.first_name + ' ' + instructor.last_name }}
                </option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="category_id">Category <span class="required">*</span></label>
              <select
                id="category_id"
                v-model="formData.category_id"
                required
              >
                <option value="">Select a category</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ category?.title || 'Category ' + category.id }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Pricing and Status -->
        <div class="form-section-container" v-show="activeSection === 1">
          <h2>Pricing and Status</h2>
          
          <div class="form-grid">
            <div class="form-group">
              <label for="price">Price <span class="required">*</span></label>
              <div class="price-input">
                <span class="currency-symbol">$</span>
                <input
                  id="price"
                  v-model.number="formData.price"
                  type="number"
                  step="0.01"
                  required
                />
              </div>
            </div>
            
            <div class="form-group">
              <label for="status">Status <span class="required">*</span></label>
              <select
                id="status"
                v-model="formData.status"
                required
              >
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div class="toggle-group">
            <div class="toggle-item">
              <label class="toggle">
                <input type="checkbox" v-model="formData.is_active">
                <span class="slider"></span>
              </label>
              <span class="toggle-label">Is Active</span>
            </div>
            
            <div class="toggle-item">
              <label class="toggle">
                <input type="checkbox" v-model="formData.is_premium">
                <span class="slider"></span>
              </label>
              <span class="toggle-label">Is Premium</span>
            </div>
            
            <div class="toggle-item">
              <label class="toggle">
                <input type="checkbox" v-model="formData.top_course">
                <span class="slider"></span>
              </label>
              <span class="toggle-label">Top Course</span>
            </div>
          </div>
        </div>

        <!-- Course Details -->
        <div class="form-section-container" v-show="activeSection === 2">
          <h2>Course Details</h2>
          
          <div class="form-group">
            <label for="science_behind_it">Science Behind It</label>
            <div class="upload-container" @click="openUploadWidget('science_behind_it')">
              <div class="upload-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
              </div>
              <div class="upload-text">
                <span class="upload-title">Upload File</span>
                <span class="upload-subtitle">Click to browse files</span>
              </div>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label for="minutes">Duration (minutes) <span class="required">*</span></label>
              <input
                id="minutes"
                v-model.number="formData.minutes"
                type="number"
                min="1"
                required
              />
            </div>
            
            <div class="form-group">
              <label for="position_day">Position Day</label>
              <input
                id="position_day"
                v-model.number="formData.position_day"
                type="number"
                min="0"
              />
            </div>
            
            <div class="form-group">
              <label for="position_night">Position Night</label>
              <input
                id="position_night"
                v-model.number="formData.position_night"
                type="number"
                min="0"
              />
            </div>
          </div>
        </div>

        <!-- Media Files -->
        <div class="form-section-container" v-show="activeSection === 3">
          <h2>Media Files</h2>
          
          <div class="upload-grid">
            <div class="form-group">
              <label for="audio_cover">Audio Cover <span class="required">*</span></label>
              <div class="upload-container" @click="openUploadWidget('audio_cover')">
                <div v-if="formData.audio_cover" class="preview-container">
                  <img :src="formData.audio_cover" alt="Cover preview" class="cover-preview" />
                  <div class="preview-overlay">Click to change</div>
                </div>
                <div v-else class="upload-content">
                  <div class="upload-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                  </div>
                  <div class="upload-text">
                    <span class="upload-title">Upload Cover Image</span>
                    <span class="upload-subtitle">Click to browse files</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="form-group">
              <label for="audio_cover_ar">Audio Cover (Arabic) <span class="required">*</span></label>
              <div class="upload-container" @click="openUploadWidget('audio_cover_ar')">
                <div v-if="formData.audio_cover_ar" class="preview-container">
                  <img :src="formData.audio_cover_ar" alt="Cover preview" class="cover-preview" />
                  <div class="preview-overlay">Click to change</div>
                </div>
                <div v-else class="upload-content">
                  <div class="upload-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                  </div>
                  <div class="upload-text">
                    <span class="upload-title">Upload Cover Image (Arabic)</span>
                    <span class="upload-subtitle">Click to browse files</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="upload-grid">
            <div class="form-group">
              <label for="audio_url">Audio File <span class="required">*</span></label>
              <div class="upload-container" @click="openUploadWidget('audio_url')">
                <div v-if="formData.audio_url" class="file-preview">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M9 18V5l12-2v13"></path>
                    <circle cx="6" cy="18" r="3"></circle>
                    <circle cx="18" cy="16" r="3"></circle>
                  </svg>
                  <span class="file-name">Audio file uploaded</span>
                  <span class="change-file">Click to change</span>
                </div>
                <div v-else class="upload-content">
                  <div class="upload-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M9 18V5l12-2v13"></path>
                      <circle cx="6" cy="18" r="3"></circle>
                      <circle cx="18" cy="16" r="3"></circle>
                    </svg>
                  </div>
                  <div class="upload-text">
                    <span class="upload-title">Upload Audio File</span>
                    <span class="upload-subtitle">Click to browse files</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="form-group">
              <label for="audio_url_ar">Audio File (Arabic) <span class="required">*</span></label>
              <div class="upload-container" @click="openUploadWidget('audio_url_ar')">
                <div v-if="formData.audio_url_ar" class="file-preview">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M9 18V5l12-2v13"></path>
                    <circle cx="6" cy="18" r="3"></circle>
                    <circle cx="18" cy="16" r="3"></circle>
                  </svg>
                  <span class="file-name">Audio file uploaded</span>
                  <span class="change-file">Click to change</span>
                </div>
                <div v-else class="upload-content">
                  <div class="upload-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M9 18V5l12-2v13"></path>
                      <circle cx="6" cy="18" r="3"></circle>
                      <circle cx="18" cy="16" r="3"></circle>
                    </svg>
                  </div>
                  <div class="upload-text">
                    <span class="upload-title">Upload Audio File (Arabic)</span>
                    <span class="upload-subtitle">Click to browse files</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-if="uploadError" class="alert alert-danger mt-3">
            {{ uploadError }}
          </div>
        </div>

        <div class="form-actions">
          <button type="button" class="btn-secondary" v-if="activeSection > 0" @click="activeSection--">
            Previous
          </button>
          <button type="button" class="btn-primary" v-if="activeSection < formSections.length - 1" @click="activeSection++">
            Next
          </button>
          <button type="submit" class="btn-success" v-if="activeSection === formSections.length - 1" :disabled="isSubmitting">
            {{ isSubmitting ? 'Creating...' : 'Create Course' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Sidebar from './Sidebar.vue'
import cloudflareR2Service from '@/services/cloudflareR2'
import QuillRichTextEditor from './QuillEditor.vue'

export default {
  name: 'CourseMaker',
  components: {
    Sidebar,
    QuillRichTextEditor
  },
  setup() {
    const router = useRouter()
    const formData = ref({
      title: '',
      title_ar: '',
      description: '',
      description_ar: '',
      instructor_id: '',
      category_id: 1,
      price: 99.99,
      status: 'active',
      is_active: true,
      science_behind_it: '',
      is_premium: true,
      minutes: 600,
      position_day: 2,
      position_night: 3,
      top_course: true,
      is_breathing: false,
      is_sleeping: false,
      sleep_id: null,
      audio_cover: '',
      audio_cover_ar: '',
      audio_url: '',
      audio_url_ar: '',
      related_field: 5
    })

    // Form sections for tabbed navigation
    const formSections = ['Basic Information', 'Pricing & Status', 'Course Details', 'Media Files']
    const activeSection = ref(0)

    const isSubmitting = ref(false)
    const successMessage = ref('')
    const errorMessage = ref('')
    const isLoading = ref(false)
    const uploadError = ref('')

    // Initialize instructors as empty array
    const instructors = ref([])

    // Initialize categories as empty array
    const categories = ref([])

    // Fetch instructors from API
    const fetchInstructors = async () => {
      isLoading.value = true
      try {
        const token = localStorage.getItem('token') || '5126|IEDUjZGv6eA7s8xTHlxqhZzAoR7SEhaamY5CC9qx0bcdaaf2'
        
        const response = await fetch('https://api.kewaar.com/api/admin/instructors1', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        const data = await response.json()
        
        if (response.ok && data.success) {
          instructors.value = data.data
        } else {
          console.error('Failed to fetch instructors:', data.message)
          errorMessage.value = 'Failed to load instructors. Please refresh the page.'
        }
      } catch (error) {
        console.error('Error fetching instructors:', error)
        errorMessage.value = 'An error occurred while loading instructors. Please refresh the page.'
      } finally {
        isLoading.value = false
      }
    }

    // Fetch categories from API
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('token') || '5126|IEDUjZGv6eA7s8xTHlxqhZzAoR7SEhaamY5CC9qx0bcdaaf2'
        
        const response = await fetch('https://api.kewaar.com/api/admin/coursecategories2', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        })

        const data = await response.json()
        
        if (response.ok && data.success) {
          categories.value = data.data.filter(category => category.is_active)
        } else {
          console.error('Failed to fetch categories:', data.message)
          errorMessage.value = 'Failed to load categories. Please refresh the page.'
        }
      } catch (error) {
        console.error('Error fetching categories:', error)
        errorMessage.value = 'An error occurred while loading categories. Please refresh the page.'
      }
    }

    // Call fetchInstructors and fetchCategories when component is mounted
    onMounted(() => {
      fetchInstructors()
      fetchCategories()
    })

    const submitCourse = async () => {
      if (isSubmitting.value) return
      
      isSubmitting.value = true
      successMessage.value = ''
      errorMessage.value = ''
      
      try {
        const token = localStorage.getItem('token') || '5126|IEDUjZGv6eA7s8xTHlxqhZzAoR7SEhaamY5CC9qx0bcdaaf2'
        
        const response = await fetch('https://api.kewaar.com/api/admin/courses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData.value)
        })

        const data = await response.json()
        
        if (response.ok) {
          successMessage.value = 'Course created successfully!'
          // Redirect to courses page after a short delay
          setTimeout(() => {
            router.push('/courses')
          }, 1500) // 1.5 second delay to show the success message
        } else {
          errorMessage.value = data.message || 'Failed to create course'
          if (data.errors) {
            errorMessage.value = Object.entries(data.errors)
              .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
              .join('\n')
          }
        }
      } catch (error) {
        console.error('Error creating course:', error)
        errorMessage.value = 'An error occurred while creating the course. Please try again.'
      } finally {
        isSubmitting.value = false
      }
    }

    // Cloudflare R2 upload handler
    const openUploadWidget = async (field) => {
      uploadError.value = ""
      
      // Create a file input element programmatically
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'video/mp4,audio/*,image/*' // Allow various file types based on field
      
      input.onchange = async (event) => {
        const file = event.target.files[0]
        if (!file) return
        
        try {
          const uploadResponse = await cloudflareR2Service.uploadWithValidation(file)
          const fileUrl = uploadResponse.fileUrl
          
          // Update the field with the file URL
          if (field === 'science_behind_it') {
            // If the field already has content, append the URL
            if (formData.value.science_behind_it && formData.value.science_behind_it.trim()) {
              formData.value.science_behind_it += '\n' + fileUrl
            } else {
              formData.value.science_behind_it = fileUrl
            }
          } else if (['audio_cover', 'audio_cover_ar', 'audio_url', 'audio_url_ar'].includes(field)) {
            // For media fields, replace the existing URL
            formData.value[field] = fileUrl
          }
        } catch (error) {
          uploadError.value = `Upload failed: ${error.message || "Unknown error"}`
        }
      }
      
      input.click()
    }

    const arabicToolbar = [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': ['', 'right', 'center', 'justify'] }],
      ['clean'],
      ['link', 'image', 'video']
    ];

    return {
      formData,
      instructors,
      categories,
      isSubmitting,
      successMessage,
      errorMessage,
      submitCourse,
      isLoading,
      openUploadWidget,
      uploadError,
      arabicToolbar,
      formSections,
      activeSection
    }
  }
}
</script>

<style scoped>
.course-maker {
  display: flex;
  min-height: 100vh;
  background-color: #f8f9fa;
}

.course-maker-content {
  flex: 1;
  padding: 30px;
  margin-left: 300px; /* Match sidebar width */
  max-width: 1200px;
}

.course-maker-header {
  margin-bottom: 30px;
}

.course-maker-header h1 {
  color: #333;
  font-weight: 600;
  font-size: 28px;
  margin: 0;
}

/* Alert messages */
.alert {
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 25px;
  font-weight: 500;
}

.alert-success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  white-space: pre-line;
}

/* Course form */
.course-form {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

/* Form tabs */
.form-tabs {
  display: flex;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  overflow-x: auto;
}

.tab-button {
  padding: 16px 24px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  color: #6c757d;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.tab-button:hover {
  color: #4361ee;
  background-color: rgba(67, 97, 238, 0.05);
}

.tab-button.active {
  color: #4361ee;
  border-bottom-color: #4361ee;
  background-color: white;
}

/* Form sections */
.form-section-container {
  padding: 30px;
}

.form-section-container h2 {
  margin-top: 0;
  margin-bottom: 24px;
  color: #343a40;
  font-size: 20px;
  font-weight: 600;
}

/* Form groups */
.form-group {
  margin-bottom: 24px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.upload-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #495057;
}

.required {
  color: #dc3545;
}

input[type="text"],
input[type="number"],
select {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 15px;
  transition: border-color 0.2s ease;
}

input[type="text"]:focus,
input[type="number"]:focus,
select:focus {
  border-color: #4361ee;
  outline: none;
  box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.15);
}

/* Price input with currency symbol */
.price-input {
  position: relative;
  display: flex;
  align-items: center;
}

.currency-symbol {
  position: absolute;
  left: 15px;
  color: #495057;
  font-weight: 500;
}

.price-input input {
  padding-left: 30px;
}

/* Toggle switches */
.toggle-group {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  border-radius: 34px;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  border-radius: 50%;
  transition: .4s;
}

input:checked + .slider {
  background-color: #4361ee;
}

input:focus + .slider {
  box-shadow: 0 0 1px #4361ee;
}

input:checked + .slider:before {
  transform: translateX(24px);
}

.toggle-label {
  font-weight: 500;
  color: #495057;
}

/* Upload containers */
.upload-container {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
}

.upload-container:hover {
  border-color: #4361ee;
  background-color: rgba(67, 97, 238, 0.03);
}

.upload-content, .file-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 25px 20px;
  text-align: center;
}

.upload-icon {
  margin-bottom: 12px;
  color: #6c757d;
}

.upload-text {
  display: flex;
  flex-direction: column;
}

.upload-title {
  font-weight: 500;
  color: #495057;
  margin-bottom: 4px;
}

.upload-subtitle {
  font-size: 13px;
  color: #6c757d;
}

.file-preview {
  color: #495057;
}

.file-name {
  font-weight: 500;
  margin: 8px 0 4px;
}

.change-file {
  font-size: 13px;
  color: #4361ee;
}

/* Image preview */
.preview-container {
  position: relative;
  width: 100%;
  height: 180px;
}

.cover-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
}

.preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 6px;
}

.preview-container:hover .preview-overlay {
  opacity: 1;
}

/* Form actions */
.form-actions {
  display: flex;
  justify-content: space-between;
  padding: 20px 30px;
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.btn-primary, .btn-secondary, .btn-success {
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-primary {
  background-color: #4361ee;
  color: white;
}

.btn-primary:hover {
  background-color: #3a56d4;
}

.btn-secondary {
  background-color: #e9ecef;
  color: #495057;
}

.btn-secondary:hover {
  background-color: #dee2e6;
}

.btn-success {
  background-color: #10b981;
  color: white;
}

.btn-success:hover {
  background-color: #0ea271;
}

.btn-success:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .course-maker-content {
    margin-left: 0;
    padding: 20px;
  }
  
  .form-grid, .upload-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .toggle-group {
    flex-direction: column;
    gap: 15px;
  }
}
</style> 